import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button } from '../ui/form';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';

interface URLImportProps {
  profileUrl: string;
  onProfileUrlChange: (url: string) => void;
  onSuccess?: () => void;
  onCancel: () => void;
  companyId?: string;
  vacantRole?: string;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  onStartImport?: (startImportFn: () => void) => void;
}

interface ScraperStatus {
  isRunning: boolean;
  isConnected: boolean;
  currentPage: any;
  currentContactData: any;
  processedCount: number;
  totalCount: number;
}

const URLImportContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ScraperProgressContainer = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const ScraperProgressTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ScraperProgressDescription = styled.p`
  margin: 0 0 1rem 0;
  color: #64748b;
  font-size: 0.875rem;
`;

const ScraperStatusCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatusLabel = styled.span`
  font-weight: 500;
  color: #475569;
`;

const StatusValue = styled.span<{ $status?: string }>`
  color: ${props => {
    switch (props.$status) {
      case 'connected':
      case 'running':
        return '#10b981';
      case 'disconnected':
      case 'stopped':
        return '#ef4444';
      default:
        return '#64748b';
    }
  }};
  font-weight: 500;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background-color: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  width: ${props => props.$progress}%;
  height: 100%;
  background-color: #10b981;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 1rem;
`;

const CurrentContactCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const ContactCardTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
`;

const ContactDataGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const ContactDataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ContactDataLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ContactDataValue = styled.span`
  font-size: 0.875rem;
  color: #1e293b;
`;

const LivePreviewContainer = styled.div`
  margin-top: 1rem;
`;

const LivePreviewHeader = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.5rem;
`;

const EmptyStateMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #64748b;
  font-size: 0.875rem;
`;

const ProcessingIndicator = styled.div`
  background: #dbeafe;
  border: 1px solid #93c5fd;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 1rem;
  color: #1d4ed8;
`;

const ControlButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
`;


const URLImport: React.FC<URLImportProps> = ({
  profileUrl,
  onProfileUrlChange,
  onSuccess,
  onCancel,
  isProcessing,
  setIsProcessing,
  onStartImport,
  companyId,
  vacantRole
}) => {
  const [scraperStatus, setScraperStatus] = useState<ScraperStatus>({
    isRunning: false,
    isConnected: false,
    currentPage: null,
    currentContactData: null,
    processedCount: 0,
    totalCount: 0
  });
  const [showScraperProgress, setShowScraperProgress] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const setupWebSocketConnection = () => {
    if (socketRef.current) return; // Already connected
    
    try {
      // Extract origin only — passing a path like /api to io() makes Socket.IO
      // treat it as a namespace, causing "Invalid namespace" errors.
      const apiUrl = import.meta.env.VITE_API_URL || '';
      let socketUrl = window.location.origin;
      try { if (apiUrl) socketUrl = new URL(apiUrl).origin; } catch { /* use default */ }
      socketRef.current = io(socketUrl, {
        path: '/socket.io/',
        transports: ['polling', 'websocket'],
        upgrade: true,
        auth: { token: localStorage.getItem('token') },
      });

      socketRef.current.on('connect_error', (err: Error) => console.warn('socket', err.message));

      socketRef.current.on('connect', () => {
        console.log('🔌 Connected to scraper WebSocket');
        setScraperStatus(prev => ({ ...prev, isConnected: true, isRunning: true }));
        socketRef.current?.emit('join-scraper-room');
      });

      socketRef.current.on('disconnect', () => {
        console.log('🔌 Disconnected from scraper WebSocket');
        setScraperStatus(prev => ({ ...prev, isConnected: false, isRunning: false }));
      });

      socketRef.current.on('page-update', (data: { url: string; html: string; contactId?: string; timestamp: string }) => {
        try {
          console.log('📄 Received page update:', data.url, 'HTML length:', data.html?.length || 0);
          setScraperStatus(prev => ({ 
            ...prev, 
            currentPage: data,
            isRunning: true
          }));
        } catch (error) {
          console.error('Error handling page update:', error);
        }
      });

      socketRef.current.on('contact-completed', async (data: { contactId: string; contactData: any; timestamp: string }) => {
        try {
          console.log('✅ Contact completed:', data.contactData);
          setScraperStatus(prev => ({ 
            ...prev, 
            currentContactData: data.contactData,
            processedCount: prev.processedCount + 1
          }));

          setScraperStatus(prev => ({ ...prev, isRunning: false }));
          setIsProcessing(false);
          setShowScraperProgress(false);
          
          // Call success callback if provided
          if (onSuccess) {
            onSuccess();
          }

        } catch (error) {
          console.error('Error handling contact completion:', error);
        }
      });

      socketRef.current.on('scraper-finished', async () => {
        try {
          console.log('🏁 Scraper finished all contacts');
        } catch (error) {
          console.error('❌ Error handling scraper finish:', error);
        }
      });

      socketRef.current.on('connect_error', (error: any) => {
        console.error('❌ WebSocket connection error:', error);
        setScraperStatus(prev => ({ ...prev, isConnected: false }));
      });

      socketRef.current.on('error', (error: any) => {
        console.error('❌ WebSocket error:', error);
      });
    } catch (error) {
      console.error('❌ Error setting up WebSocket connection:', error);
      setScraperStatus(prev => ({ ...prev, isConnected: false }));
    }
  };

  const handleStartImport = async () => {
    if (!profileUrl.trim()) {
      alert('Inserisci un URL profilo LinkedIn');
      return;
    }

    setIsProcessing(true);
    setScraperStatus(prev => ({ ...prev, totalCount: 1, processedCount: 0 }));

    try {
      const requestData: any = {
        urls: [profileUrl]
      };

      // Add company and vacancy info if provided (for vacancy mode)
      if (companyId && vacantRole) {
        requestData.companyId = companyId;
        requestData.vacantRole = vacantRole;
      }

      const response = await axios.post('/api/scraper/start-url-import', requestData);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Impossibile avviare importazione URL');
      }

      setShowScraperProgress(true);
      setupWebSocketConnection();
      
    } catch (error: any) {
      console.error('Error starting import:', error);
      alert('Errore avvio importazione: ' + (error.response?.data?.message || error.message));
      setIsProcessing(false);
    }
  };

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  // Expose handleStartImport to parent component
  useEffect(() => {
    if (onStartImport) {
      onStartImport(handleStartImport);
    }
  }, [onStartImport, handleStartImport]);

  if (showScraperProgress) {
    return (
      <ScraperProgressContainer>
        <ScraperProgressTitle>
          Scraper profilo LinkedIn
          {scraperStatus.isRunning && <span style={{ fontSize: '0.8rem', color: '#10b981' }}>● LIVE</span>}
        </ScraperProgressTitle>
        <ScraperProgressDescription>
          Estrazione delle informazioni del contatto dal profilo LinkedIn. Potrebbero volerci alcuni minuti.
        </ScraperProgressDescription>

        <ScraperStatusCard>
          <StatusRow>
            <StatusLabel>Stato connessione</StatusLabel>
            <StatusValue $status={scraperStatus.isRunning ? 'connected' : 'disconnected'}>
              {scraperStatus.isRunning ? 'Connesso' : 'Disconnesso'}
            </StatusValue>
          </StatusRow>
          <StatusRow>
            <StatusLabel>Stato scraper</StatusLabel>
            <StatusValue $status={scraperStatus.isRunning ? 'running' : 'stopped'}>
              {scraperStatus.isRunning ? 'In esecuzione' : 'Fermo'}
            </StatusValue>
          </StatusRow>
          <StatusRow>
            <StatusLabel>Contatti elaborati</StatusLabel>
            <StatusValue>{scraperStatus.processedCount || 0}</StatusValue>
          </StatusRow>
        </ScraperStatusCard>

        {scraperStatus.totalCount > 0 && (
          <>
            <ProgressBarContainer>
              <ProgressBar $progress={(scraperStatus.processedCount / scraperStatus.totalCount) * 100} />
            </ProgressBarContainer>
            <ProgressText>
              {scraperStatus.processedCount} di {scraperStatus.totalCount} profili elaborati 
              ({Math.round((scraperStatus.processedCount / scraperStatus.totalCount) * 100)}%)
            </ProgressText>
          </>
        )}

        {scraperStatus.currentContactData && (
          <CurrentContactCard>
            <ContactCardTitle>
              In elaborazione
            </ContactCardTitle>
            <ContactDataGrid>
              <ContactDataItem>
                <ContactDataLabel>Nome</ContactDataLabel>
                <ContactDataValue>
                  {scraperStatus.currentContactData.first_name} {scraperStatus.currentContactData.last_name}
                </ContactDataValue>
              </ContactDataItem>
              <ContactDataItem>
                <ContactDataLabel>Posizione</ContactDataLabel>
                <ContactDataValue>{scraperStatus.currentContactData.position || 'N/A'}</ContactDataValue>
              </ContactDataItem>
              <ContactDataItem>
                <ContactDataLabel>Azienda</ContactDataLabel>
                <ContactDataValue>{scraperStatus.currentContactData.company || 'N/A'}</ContactDataValue>
              </ContactDataItem>
              <ContactDataItem>
                <ContactDataLabel>Località</ContactDataLabel>
                <ContactDataValue>{scraperStatus.currentContactData.location || 'N/A'}</ContactDataValue>
              </ContactDataItem>
            </ContactDataGrid>
          </CurrentContactCard>
        )}

        {scraperStatus.currentPage && (
          <LivePreviewContainer>
            <LivePreviewHeader>
              Anteprima Browser Live
              <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
                Ultimo aggiornamento: {new Date(scraperStatus.currentPage.timestamp).toLocaleTimeString()}
              </div>
            </LivePreviewHeader>
            <iframe
              key={scraperStatus.currentPage.timestamp}
              srcDoc={scraperStatus.currentPage.html}
              title="LinkedIn Profile Preview"
              style={{ 
                width: '100%', 
                height: '400px', 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px',
                background: 'white' 
              }}
              sandbox=""
            />
          </LivePreviewContainer>
        )}

        {!scraperStatus.isRunning && scraperStatus.processedCount === 0 && (
          <EmptyStateMessage>
            Pronto per avviare lo scraping. Lo scraper inizierà l'elaborazione del profilo LinkedIn.
          </EmptyStateMessage>
        )}
      </ScraperProgressContainer>
    );
  }

  return (
    <URLImportContainer>
      <FormGroup>
        <Label htmlFor="profileUrl">URL profilo LinkedIn</Label>
        <Input
          id="profileUrl"
          type="url"
          value={profileUrl}
          onChange={(e) => onProfileUrlChange(e.target.value)}
          placeholder="https://www.linkedin.com/in/username"
          disabled={isProcessing}
        />
      </FormGroup>

      {isProcessing && (
        <ProcessingIndicator>
          <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
            Avvio del processo di importazione...
          </div>
          <div style={{ fontSize: '0.875rem' }}>
            Attendere l'inizializzazione dello scraper LinkedIn.
          </div>
        </ProcessingIndicator>
      )}

      <ControlButtonsContainer>
        <Button
          variant="primary"
          size="medium"
          onClick={handleStartImport}
          disabled={!profileUrl || isProcessing}
        >
          {isProcessing ? 'Avvio…' : 'Importa'}
        </Button>
        <Button
          variant="secondary"
          size="medium"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Annulla
        </Button>
      </ControlButtonsContainer>
    </URLImportContainer>
  );
};

export default URLImport;